'use client';

import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';

/**
 * Mounts the Magnet hero scene (LPMagnet) on v2-design pages, after the page has loaded and gone idle, so it never
 * competes with the first paint. It follows <html data-skin-scene> (set before first paint by the root layout, and by
 * the preview lab), and restarts its intro on the lab's 'lp-skin-replay' event.
 */
const LPMagnet = dynamic(() => import('./LPMagnet'), { ssr: false });

export function LPMagnetMount() {
  const [ready, setReady] = useState(false);
  const [on, setOn] = useState(false);
  const [replayKey, setReplayKey] = useState(0);

  // this component only renders inside V2PageTemplate, so it marks <html data-lp> (which switches the game design's CSS on)
  // for as long as a v2 page is mounted: covers client-side navigation into and out of v2 pages
  useEffect(() => {
    document.documentElement.setAttribute('data-lp', '');
    return () => document.documentElement.removeAttribute('data-lp');
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    const read = () => setOn(html.getAttribute('data-skin-scene') === 'magnet');
    read();
    const mo = new MutationObserver(read);
    mo.observe(html, { attributes: true, attributeFilter: ['data-skin-scene'] });
    const replay = () => setReplayKey((k) => k + 1);
    window.addEventListener('lp-skin-replay', replay);
    // wait for load + idle: the scene is decoration and must not cost the page its first paint
    const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
    const go = () => (w.requestIdleCallback ? w.requestIdleCallback(() => setReady(true), { timeout: 1500 }) : window.setTimeout(() => setReady(true), 300));
    if (document.readyState === 'complete') go(); else window.addEventListener('load', go, { once: true });
    return () => { mo.disconnect(); window.removeEventListener('lp-skin-replay', replay); window.removeEventListener('load', go); };
  }, []);

  // Off-screen sections pause their animations (CSS and SVG). Measured 2026-10-01: the integrations map's dots and logo
  // pulses kept animating far below the fold and caused most of the page's per-frame style work on phones. Started only
  // after load + idle (with the scene), so it never touches the page before hydration.
  useEffect(() => {
    if (!ready) return;
    const sections = [...document.querySelectorAll<HTMLElement>('.lp-root section, .lp-root > header')];
    const io = new IntersectionObserver((entries) => entries.forEach((e) => {
      const s = e.target as HTMLElement;
      const svgs = [...s.querySelectorAll<SVGSVGElement>('svg')];
      // pause/resume through the Web Animations API: no CSS rule needed (a universal "[off] *" rule made every recalc slower)
      const anims = s.getAnimations?.({ subtree: true }) ?? [];
      if (e.isIntersecting) { anims.forEach((x) => x.playState === 'paused' && x.play()); svgs.forEach((v) => v.unpauseAnimations?.()); }
      else { anims.forEach((x) => x.playState === 'running' && x.pause()); svgs.forEach((v) => v.pauseAnimations?.()); }
    }), { rootMargin: '300px 0px' });
    sections.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, [ready]);

  return ready && on ? <LPMagnet key={replayKey} replayKey={replayKey} /> : null;
}
